import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Users, Activity, FileText, Gavel, Trophy, Clock, RefreshCw, Search, Calendar,
  ChevronDown, MoreVertical, BadgeCheck, Eye, TrendingUp, TrendingDown, AlertTriangle,
  Inbox, BarChart3, X,
} from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

/**
 * Team Activity dashboard section (broker + carrier LR Direct dashboard).
 * Data: get_dashboard_activity RPC (server-side scoped, read-only, counts only).
 */

const STAT_CARDS = [
  { label: "Total Users", icon: Users, up: true, cardClass: "bg-blue-500/10 border-blue-500/20", iconClass: "bg-blue-500/15 text-blue-600" },
  { label: "Active Today", icon: Activity, up: true, cardClass: "bg-emerald-500/10 border-emerald-500/20", iconClass: "bg-emerald-500/15 text-emerald-600" },
  { label: "Loads Posted", icon: FileText, up: true, cardClass: "bg-sky-500/10 border-sky-500/20", iconClass: "bg-sky-500/15 text-sky-600" },
  { label: "Bids Placed", icon: Gavel, up: true, cardClass: "bg-violet-500/10 border-violet-500/20", iconClass: "bg-violet-500/15 text-violet-600" },
  { label: "Quotes Won", icon: Trophy, up: true, cardClass: "bg-amber-500/10 border-amber-500/20", iconClass: "bg-amber-500/15 text-amber-600" },
  { label: "Avg Response Time", icon: Clock, up: false, cardClass: "bg-rose-500/10 border-rose-500/20", iconClass: "bg-rose-500/15 text-rose-500" },
];

const ROLE_STYLES = {
  owner: "bg-blue-500/10 text-blue-600 border border-blue-500/30",
  broker: "bg-muted text-muted-foreground border border-border",
  shipper: "bg-emerald-500/10 text-emerald-600 border border-emerald-500/30",
};

const ACTIVITY_TABS = ["Loads Posted", "Bids Placed", "Logins", "Messages"];

type DailyRow = { day: string; bids: number; loads: number; quotes: number };
type ApiMember = {
  user_id: string; name: string | null; email: string | null; member_role: string | null;
  user_type: string | null; verified: boolean; bids_placed: number; bids_won: number;
  loads_posted: number; quotes_sent: number; quotes_won: number; sessions: number;
  last_active_at: string | null; daily: DailyRow[] | null;
};
type ApiFeed = { kind: string; user_id: string; at: string; detail: string | null; name: string | null };
type ApiData = { mode: "team" | "solo"; days: number; since: string; members: ApiMember[]; feed: ApiFeed[] };

const DASH = "—";
const RANGES = [7, 30, 90];
const AVATAR_TONES = [
  { avatar: "bg-blue-500/15 text-blue-600", bar: "bg-blue-500" },
  { avatar: "bg-violet-500/15 text-violet-600", bar: "bg-violet-500" },
  { avatar: "bg-amber-500/15 text-amber-600", bar: "bg-emerald-500" },
];
const FEED_STYLE: Record<string, { icon: typeof FileText; cls: string; verb: string }> = {
  bid_placed: { icon: Gavel, cls: "bg-blue-500/10 text-blue-600", verb: "submitted a bid" },
  bid_won: { icon: Trophy, cls: "bg-amber-500/10 text-amber-600", verb: "won a bid" },
  load_posted: { icon: FileText, cls: "bg-emerald-500/10 text-emerald-600", verb: "posted a new load" },
  quote_sent: { icon: Eye, cls: "bg-violet-500/10 text-violet-600", verb: "sent a quote" },
  quote_won: { icon: Trophy, cls: "bg-amber-500/10 text-amber-600", verb: "won a quote" },
};

function initials(name: string) {
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase() || "?";
}
function timeAgo(iso: string | null) {
  if (!iso) return "Not yet active";
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? "" : "s"} ago`;
}
function isToday(iso: string | null) {
  return !!iso && new Date(iso).toDateString() === new Date().toDateString();
}
function fmtDate(d: Date) {
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
const isAdminRole = (r: string | null) => r === "company_admin" || r === "super_admin";
const displayName = (name: string | null | undefined, email: string | null | undefined, fb: string) =>
  (name && name.trim()) || (email && email.trim()) || fb;
const activityCount = (m: ApiMember) => m.bids_placed + m.bids_won + m.loads_posted + m.quotes_sent + m.quotes_won;

/** Always-present mini bar chart: buckets daily counts into 12 bars with a visible baseline. */
function Sparkline({ daily, barClass }: { daily: DailyRow[]; barClass: string }) {
  const BARS = 12;
  const vals = daily.map((d) => d.bids + d.loads + d.quotes);
  const buckets: number[] = Array.from({ length: BARS }, (_, i) => {
    if (!vals.length) return 0;
    const s = Math.floor((i * vals.length) / BARS), e = Math.max(s + 1, Math.floor(((i + 1) * vals.length) / BARS));
    return vals.slice(s, e).reduce((a, b) => a + b, 0);
  });
  const max = Math.max(...buckets, 0);
  return (
    <div className="flex h-10 w-28 items-end gap-[3px] border-b border-border">
      {buckets.map((v, i) => (
        <span
          key={i}
          className={`flex-1 rounded-t-sm ${v > 0 ? barClass : "bg-muted"}`}
          style={{ height: v > 0 && max > 0 ? `${Math.max(15, (v / max) * 100)}%` : "12%" }}
        />
      ))}
    </div>
  );
}

function EmptyState({ icon: Icon, title, sub }: { icon: typeof Inbox; title: string; sub?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-4 py-8 text-center">
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <Icon className="h-5 w-5" />
      </span>
      <p className="text-sm font-semibold text-foreground">{title}</p>
      {sub && <p className="max-w-xs text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col gap-4 w-full" aria-busy="true" aria-label="Loading activity">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3"><Skeleton className="h-10 w-10 rounded-lg" /><div className="space-y-2"><Skeleton className="h-5 w-40" /><Skeleton className="h-3.5 w-72" /></div></div>
        <Skeleton className="h-10 w-40" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
        {STAT_CARDS.map((c) => <Skeleton key={c.label} className="h-[108px] rounded-xl" />)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] xl:grid-cols-[1fr_400px] gap-4">
        <Skeleton className="h-80 rounded-xl" />
        <div className="flex flex-col gap-4"><Skeleton className="h-60 rounded-xl" /><Skeleton className="h-60 rounded-xl" /></div>
      </div>
    </div>
  );
}

export function BrokerTeamActivityShowcase() {
  const { user } = useAuth();
  const userId = user?.id || "";
  const [activeTab, setActiveTab] = useState("Loads Posted");
  const [days, setDays] = useState(7);
  const [selected, setSelected] = useState<string | null>(null);
  const [roleFilter, setRoleFilter] = useState<"all" | "admin" | "member">("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "today">("all");
  const [search, setSearch] = useState("");

  const { data, refetch, isFetching, isLoading, isError } = useQuery({
    queryKey: ["dashboard-activity", userId, days],
    enabled: !!userId,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("get_dashboard_activity", { _days: days });
      if (error) throw error;
      return data as ApiData;
    },
  });

  const isTeam = data?.mode === "team";
  const allMembers = data?.members ?? [];
  const scope = useMemo(
    () => (selected ? allMembers.filter((m) => m.user_id === selected) : allMembers),
    [allMembers, selected],
  );
  const sum = (k: keyof ApiMember) => scope.reduce((a, m) => a + (Number(m[k]) || 0), 0);
  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    allMembers.forEach((m) => map.set(m.user_id, displayName(m.name, m.email, "Member")));
    return map;
  }, [allMembers]);

  const chartData = useMemo(() => {
    const map = new Map<string, { day: string; loads: number; bids: number }>();
    scope.forEach((m) => (m.daily ?? []).forEach((d) => {
      const r = map.get(d.day) ?? { day: d.day, loads: 0, bids: 0 };
      r.loads += d.loads; r.bids += d.bids; map.set(d.day, r);
    }));
    return Array.from(map.values()).sort((a, b) => a.day.localeCompare(b.day)).map((r) => {
      const [y, mo, da] = r.day.split("-").map(Number);
      const label = new Date(y, mo - 1, da, 12).toLocaleDateString("en-US", { month: "short", day: "numeric" });
      return { day: label, value: activeTab === "Loads Posted" ? r.loads : r.bids };
    });
  }, [scope, activeTab]);

  if (!userId || (isLoading && !data)) return <LoadingState />;

  if (isError && !data) {
    return (
      <div className="rounded-xl border bg-card">
        <div className="flex flex-col items-center justify-center gap-3 px-4 py-12 text-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <AlertTriangle className="h-5 w-5" />
          </span>
          <p className="text-base font-semibold text-foreground">We couldn't load activity</p>
          <p className="max-w-sm text-sm text-muted-foreground">Something went wrong while pulling the latest numbers. Please try again.</p>
          <button onClick={() => refetch()} disabled={isFetching} className="mt-1 flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-70">
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} /> Try again
          </button>
        </div>
      </div>
    );
  }

  const visibleMembers = allMembers.filter((m) => {
    if (roleFilter === "admin" && !isAdminRole(m.member_role)) return false;
    if (roleFilter === "member" && isAdminRole(m.member_role)) return false;
    if (statusFilter === "today" && !isToday(m.last_active_at)) return false;
    const q = search.trim().toLowerCase();
    if (q && !`${m.name ?? ""} ${m.email ?? ""}`.toLowerCase().includes(q)) return false;
    return true;
  }).sort((a, b) => (b.last_active_at || "").localeCompare(a.last_active_at || ""));

  const statValues: Record<string, string> = {
    "Total Users": String(scope.length),
    "Active Today": String(scope.filter((m) => isToday(m.last_active_at)).length),
    "Loads Posted": String(sum("loads_posted")),
    "Bids Placed": String(sum("bids_placed")),
    "Quotes Won": String(sum("quotes_won")),
    "Avg Response Time": DASH,
  };

  const chartTracked = activeTab === "Loads Posted" || activeTab === "Bids Placed";
  const chartEmpty = chartTracked && chartData.every((d) => !d.value);
  const feed = (data?.feed ?? []).filter((f) => !selected || f.user_id === selected).slice(0, 5);

  const endDate = new Date();
  const startDate = data?.since ? new Date(data.since) : new Date(Date.now() - (days - 1) * 864e5);
  const cycle = <T,>(arr: readonly T[], cur: T) => arr[(arr.indexOf(cur) + 1) % arr.length];
  const selectedName = selected ? nameById.get(selected) : null;
  const roleLabel = { all: "All Roles", admin: "Owner / Admin", member: "Members" }[roleFilter];
  const statusLabel = { all: "All Statuses", today: "Active Today" }[statusFilter];
  const pill = "flex items-center gap-2 rounded-lg border border-border bg-card px-3.5 py-2 text-sm font-medium text-foreground";

  return (
    <div className="flex flex-col gap-4 xl:gap-5 w-full">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Users className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground leading-tight">Team Activity</h2>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {isTeam ? "Monitor usage, engagement, and performance across your team." : "Your usage, engagement, and performance."}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setDays(cycle(RANGES, days))} className={pill}>
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="flex flex-col items-start leading-tight">
              <span className="font-semibold">Last {days} days</span>
              <span className="text-[11px] text-muted-foreground">{fmtDate(startDate)} – {fmtDate(endDate)}</span>
            </span>
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </button>
          <button onClick={() => setRoleFilter(cycle(["all", "admin", "member"] as const, roleFilter))} className={pill}>
            {roleLabel} <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </button>
          <button onClick={() => setStatusFilter(statusFilter === "all" ? "today" : "all")} className={pill}>
            {statusLabel} <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </button>
          <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-3.5 py-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search team members..."
              className="w-40 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none" />
          </div>
          <button onClick={() => refetch()} disabled={isFetching} className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-70">
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} /> Refresh Activity
          </button>
        </div>
      </div>

      {isError && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-2.5 text-sm text-foreground">
          <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-destructive" /> Couldn't refresh — showing the last loaded numbers.</span>
          <button onClick={() => refetch()} className="font-semibold text-primary hover:underline">Retry</button>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
        {STAT_CARDS.map((card) => (
          <div key={card.label} className={`rounded-xl border p-4 ${card.cardClass}`}>
            <div className="flex items-center gap-2.5">
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${card.iconClass}`}>
                <card.icon className="h-4 w-4" />
              </span>
              <span className="text-xs font-medium text-muted-foreground truncate">{card.label}</span>
            </div>
            <div className="mt-3 text-3xl font-extrabold text-foreground leading-none">{statValues[card.label]}</div>
            <div className="mt-2 flex items-center gap-1 text-xs font-medium text-muted-foreground">
              {card.up ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />} {DASH}
            </div>
          </div>
        ))}
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 gap-4 xl:gap-5 items-start lg:grid-cols-[1fr_340px] xl:grid-cols-[1fr_400px]">
        {/* Member list (team: all members; solo: just you) */}
        <div className="rounded-xl border bg-card min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b px-5 py-4">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-bold text-foreground">
                Team Member Activity <span className="text-muted-foreground font-semibold">({allMembers.length})</span>
              </h3>
              {selectedName && (
                <button onClick={() => setSelected(null)} className="flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary" aria-label="Show all members">
                  Viewing {selectedName} <X className="h-3 w-3" />
                </button>
              )}
            </div>
            <button className="flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground">
              Sort by: <span className="font-semibold text-foreground">Last Active</span> <ChevronDown className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="divide-y divide-border">
            {visibleMembers.length === 0 && (
              <EmptyState icon={Search} title="No team members match" sub="Try clearing the search or role and status filters." />
            )}
            {visibleMembers.map((m, idx) => {
              const tone = AVATAR_TONES[idx % AVATAR_TONES.length];
              const name = displayName(m.name, m.email, "Member");
              const roles = [
                ...(isAdminRole(m.member_role) ? [{ label: "Owner / Admin", className: ROLE_STYLES.owner }] : []),
                { label: m.user_type === "carrier" ? "Carrier" : m.user_type === "shipper" ? "Shipper" : "Broker", className: m.user_type === "shipper" ? ROLE_STYLES.shipper : ROLE_STYLES.broker },
              ];
              const winRate = m.bids_placed > 0 ? `${Math.round((m.bids_won / m.bids_placed) * 100)}%` : DASH;
              const stats = [
                { value: String(m.bids_placed), label: "Bids Placed" },
                { value: String(m.bids_won), label: "Bids Won" },
                { value: winRate, label: "Win Rate" },
                { value: String(m.loads_posted), label: "Loads Posted" },
                { value: String(m.quotes_sent), label: "Quotes Sent" },
                { value: String(m.quotes_won), label: "Quotes Won" },
                { value: DASH, label: "Searches" },
                { value: DASH, label: "Messages" },
                { value: String(m.sessions), label: "Sessions" },
                { value: DASH, label: "Avg. Session" },
                { value: DASH, label: "Devices" },
              ];
              const noActivity = activityCount(m) === 0;
              const isSel = selected === m.user_id;
              const toggle = () => isTeam && setSelected(isSel ? null : m.user_id);
              const verifiedBox = m.verified ? (
                <div className="flex items-center justify-center gap-1.5 rounded-lg bg-emerald-500/10 px-3 py-2.5 text-emerald-600">
                  <BadgeCheck className="h-4 w-4" /><span className="text-xs font-semibold">Verified</span>
                </div>
              ) : (
                <div className="flex items-center justify-center rounded-lg bg-muted/60 px-3 py-2.5 text-xs font-medium text-muted-foreground">Not verified</div>
              );
              return (
                <div key={m.user_id}
                  role={isTeam ? "button" : undefined} tabIndex={isTeam ? 0 : undefined}
                  onClick={toggle} onKeyDown={(e) => { if (e.key === "Enter") toggle(); }}
                  className={`p-5 transition-colors ${isTeam ? "cursor-pointer" : ""} ${isSel ? "bg-primary/5 ring-1 ring-inset ring-primary/40" : isTeam ? "hover:bg-muted/30" : ""}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="relative shrink-0">
                        <span className={`flex h-12 w-12 items-center justify-center rounded-full text-sm font-bold ${tone.avatar}`}>{initials(name)}</span>
                        {isToday(m.last_active_at) && <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-card bg-emerald-500" />}
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="text-base font-bold text-foreground truncate">{name}</span>
                          {roles.map((r) => <span key={r.label} className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${r.className}`}>{r.label}</span>)}
                        </div>
                        {m.email && m.email !== name && <p className="text-xs text-muted-foreground truncate">{m.email}</p>}
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="hidden sm:flex items-center gap-4">
                        <Sparkline daily={m.daily ?? []} barClass={tone.bar} />
                        <div className="text-right">
                          <span className="text-[11px] text-muted-foreground">Last active</span>
                          <div className={`text-xs font-semibold ${isToday(m.last_active_at) ? "text-emerald-600" : "text-foreground"}`}>{timeAgo(m.last_active_at)}</div>
                        </div>
                      </div>
                      <button className="rounded-md p-1 text-muted-foreground hover:bg-muted" onClick={(e) => e.stopPropagation()} aria-label="More">
                        <MoreVertical className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="mt-2 flex items-center justify-between sm:hidden text-xs">
                    <span className="text-muted-foreground">Last active</span>
                    <span className="font-semibold text-foreground">{timeAgo(m.last_active_at)}</span>
                  </div>

                  {noActivity ? (
                    <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="flex flex-1 items-center gap-3 rounded-lg border border-dashed border-border bg-muted/30 px-4 py-3">
                        <Inbox className="h-5 w-5 shrink-0 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-semibold text-foreground">No activity in the last {days} days</p>
                          <p className="text-xs text-muted-foreground">Bids, loads and quotes will show here once {name.split(" ")[0]} starts using LaneRunner.</p>
                        </div>
                      </div>
                      <div className="sm:w-36">{verifiedBox}</div>
                    </div>
                  ) : (
                    <div className="mt-4 grid grid-cols-3 sm:grid-cols-6 gap-2">
                      {stats.map((s) => (
                        <div key={s.label} className="rounded-lg bg-muted/50 px-3 py-2.5">
                          <div className="text-base font-bold text-foreground leading-tight">{s.value}</div>
                          <div className="text-[11px] text-muted-foreground truncate">{s.label}</div>
                        </div>
                      ))}
                      {verifiedBox}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right column */}
        <div className="flex flex-col gap-4 xl:gap-5 min-w-0">
          <div className="rounded-xl border bg-card">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b px-5 py-4">
              <h3 className="text-base font-bold text-foreground">Activity Over Time</h3>
              <div className="flex items-center gap-1 rounded-lg bg-muted/60 p-1">
                {ACTIVITY_TABS.map((tab) => (
                  <button key={tab} onClick={() => setActiveTab(tab)}
                    className={`rounded-md px-2.5 py-1 text-[11px] font-semibold transition-colors ${activeTab === tab ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>
                    {tab}
                  </button>
                ))}
              </div>
            </div>
            <div className="p-5">
              <div className="relative h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 4, right: 0, left: -22, bottom: 0 }}>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="day" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip cursor={{ fill: "hsl(var(--muted) / 0.5)" }} />
                    {chartTracked && <Bar dataKey="value" name={activeTab} fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={22} />}
                  </BarChart>
                </ResponsiveContainer>
                {(!chartTracked || chartEmpty) && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="rounded-lg bg-card/90 px-4 py-2 text-center">
                      <BarChart3 className="mx-auto h-5 w-5 text-muted-foreground" />
                      <p className="mt-1 text-sm font-semibold text-foreground">
                        {chartTracked ? `No activity in the last ${days} days` : `${activeTab} not tracked yet`}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-3 flex items-center justify-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-blue-500" /> {activeTab}</span>
              </div>
            </div>
          </div>

          <div className="rounded-xl border bg-card">
            <div className="border-b px-5 py-4">
              <h3 className="text-base font-bold text-foreground">Recent Team Activity</h3>
            </div>
            {feed.length === 0 ? (
              <EmptyState icon={Inbox} title={`No activity in the last ${days} days`} sub="New bids, loads and quotes will appear here." />
            ) : (
              <div className="divide-y divide-border">
                {feed.map((item, i) => {
                  const st = FEED_STYLE[item.kind] ?? FEED_STYLE.bid_placed;
                  const Icon = st.icon;
                  const who = nameById.get(item.user_id) || displayName(item.name, null, "Someone");
                  return (
                    <div key={`${item.kind}-${item.at}-${i}`} className="flex items-center gap-3 px-5 py-3.5">
                      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${st.cls}`}><Icon className="h-4 w-4" /></span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-foreground truncate">{who} {st.verb}</p>
                        <p className="text-xs text-muted-foreground truncate">{item.detail || DASH}</p>
                      </div>
                      <span className="shrink-0 text-[11px] text-muted-foreground">{timeAgo(item.at)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
